//
// Copyright 2022 DXOS.org
//

import { create } from '@bufbuild/protobuf';

import { Event, scheduleTask, synchronized } from '@dxos/async';
import { Context } from '@dxos/context';
import { invariant } from '@dxos/invariant';
import { PublicKey } from '@dxos/keys';
import { log } from '@dxos/log';
import { type PeerInfo } from '@dxos/messaging';
import { CancelledError, SystemError } from '@dxos/protocols';
import { type Answer, AnswerSchema, CloseSchema, OfferSchema } from '@dxos/protocols/buf/dxos/mesh/swarm_pb';

import { type CloseMessage, type OfferMessage, type SignalMessage, type SignalMessenger } from '../signal/index.ts';
import { type TransportFactory } from '../transport/index.ts';
import { type WireProtocolProvider } from '../wire-protocol.ts';
import { type ConnectionLimiter } from './connection-limiter.ts';
import { Connection, ConnectionState } from './connection.ts';

export class ConnectionDisplacedError extends SystemError {
  constructor() {
    super({ message: 'Connection displaced by remote initiator.' });
  }
}

export class SessionClosedByRemoteError extends SystemError {
  constructor(reason: string) {
    super({ message: `Session closed by the remote peer: ${reason}` });
  }
}

interface PeerCallbacks {
  /**
   * Connection attempt initiated.
   */
  onInitiated: (connection: Connection) => void;

  /**
   * Connection opened.
   */
  onConnected: () => void;

  /**
   * Connection closed.
   */
  onDisconnected: () => void;

  /**
   * Peer accepted our offer to connect.
   */
  onAccepted: () => void;

  /**
   * Peer rejected our offer to connect.
   */
  onRejected: () => void;

  /**
   * Returns true if the remote peer's offer should be accepted.
   */
  onOffer: (remote: PeerInfo) => Promise<boolean>;

  /**
   * Peer is available to connect.
   */
  onPeerAvailable: () => void;
}

/**
 * Minimum time the connection needs to be open to not incur a cool-down timer for this peer after the connection closes.
 */
const CONNECTION_COUNTS_STABLE_AFTER = 5_000;

/**
 * State of remote peer during the lifetime of a swarm connection.
 * Can open and close multiple connections to the remote peer.
 */
export class Peer {
  /**
   * Will be available to connect after this time.
   */
  private _availableAfter = 0;
  public availableToConnect = true;
  private _lastConnectionTime?: number;

  private readonly _ctx = new Context();
  private _connectionCtx?: Context;

  public connection?: Connection;

  /**
   * Whether the peer is currently advertizing itself on the signal-network.
   */
  public advertizing = false;

  public initiating = false;

  public readonly connectionDisplaced = new Event<Connection>();

  constructor(
    public readonly remoteInfo: PeerInfo,
    public readonly topic: PublicKey,
    public readonly localInfo: PeerInfo,
    private readonly _signalMessaging: SignalMessenger,
    private readonly _protocolProvider: WireProtocolProvider,
    private readonly _transportFactory: TransportFactory,
    private readonly _connectionLimiter: ConnectionLimiter,
    private readonly _callbacks: PeerCallbacks,
  ) {}

  /**
   * Respond to remote offer.
   */
  async onOffer(_ctx: Context, message: OfferMessage): Promise<Answer> {
    const remote = message.author;

    if (
      this.connection &&
      ![ConnectionState.CREATED, ConnectionState.INITIAL, ConnectionState.CONNECTING].includes(this.connection.state)
    ) {
      log.info(`received offer when connection already in ${this.connection.state} state`);
      return create(AnswerSchema, { accept: false });
    }
    // Check if we are already trying to connect to that peer.
    if (this.connection || this.initiating) {
      // Determine the "polite" peer (the one that will accept offers).
      // Peer with the highest Id closes its connection, and accepts remote peer's offer.
      if (remote.peerKey < this.localInfo.peerKey) {
        // TODO(nf): Gets stuck when remote connection is aborted (i.e. closed tab).
        log('close local connection', {
          localPeer: this.localInfo,
          topic: this.topic,
          remotePeer: this.remoteInfo,
          sessionId: this.connection?.sessionId,
        });

        if (this.connection) {
          // Close our connection and accept remote peer's connection.
          await this.closeConnection(new ConnectionDisplacedError());
        }
      } else {
        // Continue with our origination attempt, the remote peer will close its connection and accept ours.
        return create(AnswerSchema, { accept: false });
      }
    }

    if (await this._callbacks.onOffer(remote)) {
      if (!this.connection) {
        // Connection might have been already established.
        invariant(message.sessionId);
        const connection = this._createConnection(false, message.sessionId);

        try {
          await this._connectionLimiter.connecting(message.sessionId);
          connection.initiate();

          await connection.openConnection();
        } catch (err: any) {
          if (!(err instanceof CancelledError)) {
            log.info('connection error', { topic: this.topic, peerId: this.localInfo, remoteId: this.remoteInfo, err });
          }

          // Calls `onStateChange` with CLOSED state.
          await this.closeConnection(err);
        }

        return create(AnswerSchema, { accept: true });
      }
    }

    return create(AnswerSchema, { accept: false });
  }

  /**
   * Initiate a connection to the remote peer.
   */
  async initiateConnection(ctx: Context): Promise<void> {
    invariant(!this.initiating, 'Initiation in progress.');
    invariant(!this.connection, 'Already connected.');
    const sessionId = PublicKey.random();
    log('initiating...', { local: this.localInfo, topic: this.topic, remote: this.remoteInfo, sessionId });

    const connection = this._createConnection(true, sessionId);
    this.initiating = true;

    let answer: Answer;
    try {
      await this._connectionLimiter.connecting(sessionId);
      connection.initiate();

      answer = await this._signalMessaging.offer(ctx, {
        author: this.localInfo,
        recipient: this.remoteInfo,
        sessionId,
        topic: this.topic,
        data: { offer: create(OfferSchema, {}) },
      });
      log('received', { answer, topic: this.topic, local: this.localInfo, remote: this.remoteInfo });
      if (connection.state !== ConnectionState.INITIAL) {
        log('ignoring response');
        return;
      }
    } catch (err: any) {
      log('initiation error: send offer', { err, topic: this.topic, local: this.localInfo, remote: this.remoteInfo });
      await connection.abort(err);
      throw err;
    } finally {
      this.initiating = false;
    }

    try {
      if (!answer.accept) {
        this._callbacks.onRejected();
        return;
      }
    } catch (err: any) {
      log('initiation error: accept answer', {
        err,
        topic: this.topic,
        local: this.localInfo,
        remote: this.remoteInfo,
      });
      await connection.abort(err);
      throw err;
    } finally {
      this.initiating = false;
    }

    try {
      log('opening connection as initiator');
      await connection.openConnection();
      this._callbacks.onAccepted();
    } catch (err: any) {
      log('initiation error: open connection', {
        err,
        topic: this.topic,
        local: this.localInfo,
        remote: this.remoteInfo,
      });
      // TODO(nf): unsure when this will be called and the connection won't abort itself. but if it does fall through we should probably abort and not close.
      log.warn('closing connection due to unhandled error on openConnection', { err });
      // Calls `onStateChange` with CLOSED state.
      await this.closeConnection(err);
      throw err;
    } finally {
      this.initiating = false;
    }
  }

  /**
   * Create new connection.
   * Either we're initiating a connection or creating one in response to an offer from the other peer.
   */
  private _createConnection(initiator: boolean, sessionId: PublicKey): Connection {
    log('creating connection', {
      topic: this.topic,
      peerId: this.localInfo,
      remoteId: this.remoteInfo,
      initiator,
      sessionId,
    });
    invariant(!this.connection, 'Already connected.');

    let connected = false;
    const connection = new Connection(
      this.topic,
      this.localInfo,
      this.remoteInfo,
      sessionId,
      initiator,
      this._signalMessaging,
      // TODO(dmaretskyi): Init only when connection is established.
      this._protocolProvider({
        initiator,
        localPeerId: PublicKey.from(this.localInfo.peerKey),
        remotePeerId: PublicKey.from(this.remoteInfo.peerKey),
        topic: this.topic,
      }),
      this._transportFactory,
      {
        onConnected: () => {
          connected = true;
          this.availableToConnect = true;
          this._lastConnectionTime = Date.now();
          this._callbacks.onConnected();

          this._connectionLimiter.doneConnecting(sessionId);
          log.trace('dxos.mesh.connection.connected', {
            topic: this.topic,
            localPeerId: this.localInfo,
            remotePeerId: this.remoteInfo,
            sessionId,
            initiator,
          });
        },
        onClosed: (err) => {
          const logMeta = { topic: this.topic, peerId: this.localInfo, remoteId: this.remoteInfo, initiator };
          log('connection closed', logMeta);

          // Make sure none of the connections are stuck in the limiter.
          this._connectionLimiter.doneConnecting(sessionId);

          invariant(this.connection === connection, 'Connection mismatch (race condition).');

          log.trace('dxos.mesh.connection.closed', {
            topic: this.topic,
            localPeerId: this.localInfo,
            remotePeerId: this.remoteInfo,
            sessionId,
            initiator,
          });

          // The remote otherwise waits out its transport timeout on a session only this side knows is over.
          if (
            !connected &&
            !(err instanceof ConnectionDisplacedError) &&
            !(err instanceof SessionClosedByRemoteError)
          ) {
            this._announceClose(sessionId, err);
          }

          if (err instanceof ConnectionDisplacedError) {
            this.connectionDisplaced.emit(this.connection);
          } else {
            if (this._lastConnectionTime && this._lastConnectionTime + CONNECTION_COUNTS_STABLE_AFTER < Date.now()) {
              // If we're closing the connection, and it has been connected for a while, reset the backoff.
              this._availableAfter = 0;
            } else {
              this.availableToConnect = false;
              this._availableAfter = increaseInterval(this._availableAfter);
            }

            this._callbacks.onDisconnected();

            scheduleTask(
              this._connectionCtx!,
              () => {
                log('peer became available', logMeta);
                this.availableToConnect = true;
                this._callbacks.onPeerAvailable();
              },
              this._availableAfter,
            );
          }

          this.connection = undefined;
        },
      },
    );
    this._callbacks.onInitiated(connection);

    void this._connectionCtx?.dispose();
    this._connectionCtx = this._ctx.derive();

    connection.errors.handle((err) => {
      if (this.connection !== connection) {
        log('error from a connection this peer no longer holds', { sessionId, err });
        return;
      }

      log.info('connection error, closing', {
        topic: this.topic,
        peerId: this.localInfo,
        remoteId: this.remoteInfo,
        initiator,
        err,
      });
      log.trace('dxos.mesh.connection.error', {
        topic: this.topic,
        localPeerId: this.localInfo,
        remotePeerId: this.remoteInfo,
        sessionId,
        initiator,
        err,
      });

      // Calls `onStateChange` with CLOSED state.
      void this.closeConnection(err);
    });

    this.connection = connection;

    return connection;
  }

  async closeConnection(err?: Error): Promise<void> {
    if (!this.connection) {
      return;
    }

    const connection = this.connection;

    log('closing...', { peerId: this.remoteInfo, sessionId: connection.sessionId });

    // Triggers `onStateChange` callback which will clean up the connection.
    // Won't throw.
    await connection.close({ error: err });

    log('closed', { peerId: this.remoteInfo, sessionId: connection.sessionId });
  }

  async onClose(_ctx: Context, message: CloseMessage): Promise<void> {
    if (!this.connection?.sessionId.equals(message.sessionId)) {
      log('dropping close for a session not in progress', { sessionId: message.sessionId });
      return;
    }

    // The remote only announces a session that died before connecting; once connected, its own close ends it.
    if (
      ![ConnectionState.CREATED, ConnectionState.INITIAL, ConnectionState.CONNECTING].includes(this.connection.state)
    ) {
      log('dropping close for a session that already connected', { sessionId: message.sessionId });
      return;
    }

    const reason = message.data.close.reason;
    log.info('remote peer closed the session before it connected', { sessionId: message.sessionId, reason });
    await this.closeConnection(new SessionClosedByRemoteError(reason));
  }

  async onSignal(ctx: Context, message: SignalMessage): Promise<void> {
    if (!this.connection) {
      log('dropping signal message for non-existent connection', { message });
      return;
    }

    await this.connection.signal(ctx, message);
  }

  @synchronized
  async safeDestroy(reason?: string): Promise<void> {
    await this._ctx.dispose();
    log('Destroying peer', { peerId: this.remoteInfo, topic: this.topic });

    // Won't throw.
    await this?.connection?.close({ reason });
  }

  /**
   * Sent on this peer's context, so a peer being destroyed announces nothing: leaving the swarm already
   * tells the remote the session is over.
   */
  private _announceClose(sessionId: PublicKey, err?: Error): void {
    this._signalMessaging
      .close(this._ctx, {
        author: this.localInfo,
        recipient: this.remoteInfo,
        topic: this.topic,
        sessionId,
        data: { close: create(CloseSchema, { reason: err?.message ?? 'transport closed' }) },
      })
      .catch((err) => log('session close not delivered', { sessionId, err }));
  }
}

const increaseInterval = (interval: number) => {
  if (interval === 0) {
    return 50;
  } else if (interval < 500) {
    return 500;
  } else if (interval < 1000) {
    return 1000;
  } else if (interval < 5_000) {
    return 5_000;
  }
  return 10_000;
};
